import { actorTypeLabel } from './MerkeModell/detalj_api'
import i18n from '../../i18n'
import type { KjoperKlasseAndel } from './sammenligningApi'

/**
 * Display helpers for the buyer-class mix in the Sammenligning grid (#594 AC5).
 *
 * The backend returns the BUYER half of class_ as a raw token (PERSON, FIRMA,
 * BILUTLEIE). The seller half is dropped on purpose: on bruktbil it is
 * BILBRANSJE on practically every row, so showing it would spend half the
 * column repeating a value that is identical on every line.
 */

/**
 * Norwegian label for a buyer token.
 *
 * Reuses the existing actor-type labels so this column cannot drift from
 * the rest of the module. An unrecognised token — a new buyer kind appearing
 * upstream — falls back to its own capitalised spelling rather than being
 * hidden, so it shows up as data instead of vanishing.
 */
export function kjoperKlasseLabel(klasse: string): string {
  const known = actorTypeLabel(klasse)
  if (known) return known
  return klasse.charAt(0).toUpperCase() + klasse.slice(1).toLowerCase()
}

/** "62,4 %" in Norwegian formatting — one decimal, non-breaking space. */
export function formatAndel(andel: number): string {
  return `${andel.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
}

/**
 * The whole mix as one line: "Privatperson 62,4 % · Firma 21,1 %".
 *
 * An org with no classified transfers yields an em dash rather than an empty
 * cell, matching how the other columns report absence.
 */
export function formatKjoperKlasser(klasser: KjoperKlasseAndel[] | null | undefined): string {
  const list = klasser ?? []
  if (list.length === 0) return '—'
  return list.map(k => `${kjoperKlasseLabel(k.klasse)} ${formatAndel(k.andel)}`).join(' · ')
}

/** "4,2 år", or an em dash when the figure is absent. Never renders 0 for absent. */
export function formatAar(value: number | null | undefined): string {
  if (value == null) return '—'
  return i18n.t('units.aar', {
    ns: 'bruktbil',
    value: value.toLocaleString('nb-NO', { maximumFractionDigits: 1 }),
  })
}
